import dayjs from 'dayjs'
import { type ComputedRef, type Ref, computed, nextTick, ref, watch } from 'vue'
import { createEventHook, useDebounceFn } from '@vueuse/core'
import type { TimelineZoomLevel } from '../utils/timelineUtils'
import { TIMELINE_ZOOM_LEVELS, isFortnightMonday, isGridlineBoundary } from '../utils/timelineUtils'

// Per-scale config drives column width, buffer size, prev/next step, and
// header rendering. `colWidth` is the fixed pixel width of one day-column.
// `bufferDays` is how many days each side of the anchor we render initially
// (and extend by when the user nears an edge).
//
// Header layout has up to N stacked major rows + one minor (per-day) row.
// `majorTiers` lists the major rows top-down — e.g. `['quarter', 'month']`
// shows "Q2 2026" over "April / May / June". Empty array = no major row.
// `minorLabel` controls per-day cell labels in the bottom row.
type MajorTier = 'year' | 'quarter' | 'month'

export interface ScaleConfig {
  colWidth: number
  bufferDays: number
  navUnit: 'day' | 'week' | 'month' | 'year'
  navAmount: number
  majorTiers: MajorTier[]
  minorLabel:
    | 'weekday-full'
    | 'weekday-short'
    | 'weekday-letter'
    | 'day-number'
    | 'mondays'
    | 'fortnight'
    | 'quarter-month'
    | 'none'
  // Gridline cadence — only render vertical lines at boundaries of this unit.
  // At fine zooms this matches the day cells; at coarse zooms it strips away
  // daily gridlines so the grid reads as week/fortnight/month/quarter chunks.
  gridlineUnit: 'day' | 'week' | 'fortnight' | 'month' | 'quarter'
}

export const SCALE_CONFIG: Record<TimelineZoomLevel, ScaleConfig> = {
  'day': {
    colWidth: 160,
    bufferDays: 30,
    navUnit: 'day',
    navAmount: 1,
    majorTiers: [],
    minorLabel: 'weekday-full',
    gridlineUnit: 'day',
  },
  'week': {
    colWidth: 72,
    bufferDays: 60,
    navUnit: 'week',
    navAmount: 1,
    majorTiers: [],
    minorLabel: 'weekday-short',
    gridlineUnit: 'day',
  },
  '2week': {
    colWidth: 56,
    bufferDays: 90,
    navUnit: 'week',
    navAmount: 2,
    majorTiers: ['month'],
    minorLabel: 'weekday-letter',
    gridlineUnit: 'day',
  },
  'month': {
    colWidth: 36,
    bufferDays: 120,
    navUnit: 'month',
    navAmount: 1,
    majorTiers: [],
    minorLabel: 'weekday-letter',
    gridlineUnit: 'day',
  },
  'quarter': {
    colWidth: 12,
    bufferDays: 365,
    navUnit: 'month',
    navAmount: 3,
    majorTiers: ['quarter', 'month'],
    minorLabel: 'mondays',
    gridlineUnit: 'week',
  },
  '6month': {
    colWidth: 6,
    bufferDays: 540,
    navUnit: 'month',
    navAmount: 6,
    majorTiers: ['quarter', 'month'],
    minorLabel: 'mondays',
    gridlineUnit: 'week',
  },
  'year': {
    colWidth: 4,
    bufferDays: 730,
    navUnit: 'year',
    navAmount: 1,
    majorTiers: ['month'],
    minorLabel: 'fortnight',
    gridlineUnit: 'fortnight',
  },
  '2year': {
    colWidth: 2,
    bufferDays: 1095,
    navUnit: 'year',
    navAmount: 2,
    majorTiers: ['year', 'quarter'],
    minorLabel: 'quarter-month',
    gridlineUnit: 'month',
  },
  '5year': {
    colWidth: 1,
    bufferDays: 1825,
    navUnit: 'year',
    navAmount: 5,
    majorTiers: ['year', 'quarter'],
    minorLabel: 'quarter-month',
    gridlineUnit: 'quarter',
  },
}

const quarterOf = (d: dayjs.Dayjs): number => Math.floor(d.month() / 3) + 1

// Extend the buffer once the user scrolls within this many pixels of an edge.
const EXTEND_THRESHOLD_PX = 240

// Buffer span = bufferDays * MAX_BUFFER_MULTIPLIER. The initial span is
// 2 × bufferDays (±bufferDays around the centre), and we want the buffer
// to *slide* on every edge-extension rather than grow then slide — so
// the fetch window's start advances together with its end as the user
// scrolls. Setting this to 2 makes maxSpan equal to the initial span,
// which trips the opposite-side trim on every extension.
const MAX_BUFFER_MULTIPLIER = 2

// Module-level cache to persist navigation state across view switches.
// Keyed by view ID — view IDs are globally unique (nanoid-prefixed by
// feature: `tlv` for Timeline, `gtv` for Gantt) so Timeline and Gantt
// safely share this single cache.
const MAX_CACHED_VIEWS = 50
const _viewStateCache = new Map<string, { currentDate: string; zoomLevel: TimelineZoomLevel }>()
const _initializedViews = new Set<string>()

const lruViewCacheGet = (viewId: string) => {
  const entry = _viewStateCache.get(viewId)
  if (entry) {
    _viewStateCache.delete(viewId)
    _viewStateCache.set(viewId, entry)
  }
  return entry
}

const lruViewCacheSet = (viewId: string, entry: { currentDate: string; zoomLevel: TimelineZoomLevel }) => {
  if (_viewStateCache.has(viewId)) _viewStateCache.delete(viewId)
  _viewStateCache.set(viewId, entry)
  while (_viewStateCache.size > MAX_CACHED_VIEWS) {
    const oldest = _viewStateCache.keys().next().value
    if (oldest === undefined) break
    _viewStateCache.delete(oldest)
    _initializedViews.delete(oldest)
  }
}

export interface UseDateAxisStateOptions {
  // Reactive view id — used as the LRU cache key and to detect view switches.
  viewId: Ref<string | undefined> | ComputedRef<string | undefined>
  // Subset of zoom levels the consumer wants to allow. Defaults to all 9.
  // Cached zoom levels that fall outside this list are ignored on restore.
  zoomLevels?: readonly TimelineZoomLevel[]
  // Starting zoom when no cached entry exists.
  initialZoom?: TimelineZoomLevel
}

/**
 * Date-axis state shared by Timeline and Gantt views.
 *
 * Owns the scale/buffer/scroll/viewport model:
 * - 9 supported zoom levels with per-scale `SCALE_CONFIG` (column width,
 *   buffer size, navigation step, header tiers, gridline cadence).
 * - A sliding date buffer (`bufferStart`/`bufferEnd`) that grows by `bufferDays`
 *   on edge proximity and trims the opposite side to keep total span bounded.
 * - `scrollLeft`/`viewportWidth` driven by the grid component, used to derive
 *   `currentDate` (centre of the viewport) and to compute scroll targets.
 * - Sparse overlays (`gridlineOffsets`, `weekendOffsets`, `minorLabels`,
 *   `majorHeaderTiers`) so coarse zooms don't iterate thousands of cells.
 * - An LRU cache that persists `(zoomLevel, currentDate)` per view across
 *   view switches.
 *
 * Caller (Timeline/Gantt store) attaches data-fetching to `bufferStart` /
 * `bufferEnd` changes and decides what "initial view" means for its data.
 */
export function useDateAxisState(options: UseDateAxisStateOptions) {
  const { viewId, initialZoom = 'month' } = options
  const allowedZoomLevels: readonly TimelineZoomLevel[] = options.zoomLevels ?? TIMELINE_ZOOM_LEVELS

  // ---- Zoom + current position ----

  const zoomLevel = ref<TimelineZoomLevel>(allowedZoomLevels.includes(initialZoom) ? initialZoom : allowedZoomLevels[0])

  // `currentDate` is the date currently centred in the viewport. It is
  // primarily *derived* from scroll position (via `onScrollUpdate`), but
  // explicit navigation actions (date picker, today, prev/next) write to
  // it as well. It drives the breadcrumb label and is what gets cached.
  const currentDate = ref<dayjs.Dayjs>(dayjs())

  const selectedDate = ref<dayjs.Dayjs>(dayjs())

  // ---- Buffered window ----

  const initialBufferDays = SCALE_CONFIG[zoomLevel.value].bufferDays
  const bufferStart = ref<dayjs.Dayjs>(currentDate.value.startOf('day').subtract(initialBufferDays, 'day'))
  const bufferEnd = ref<dayjs.Dayjs>(currentDate.value.startOf('day').add(initialBufferDays, 'day'))

  // Scroll position (px from buffer start) and viewport width — set by the
  // grid component. The store reads these to derive `currentDate` and to
  // compute scroll targets for goToDate / today / prev / next.
  const scrollLeft = ref(0)
  const viewportWidth = ref(0)

  // Per-scale fixed column width.
  const colWidth = computed(() => SCALE_CONFIG[zoomLevel.value].colWidth)

  const visibleDates = computed<dayjs.Dayjs[]>(() => {
    const dates: dayjs.Dayjs[] = []
    let d = bufferStart.value
    // bufferEnd is inclusive
    while (!d.isAfter(bufferEnd.value, 'day')) {
      dates.push(d)
      d = d.add(1, 'day')
    }
    return dates
  })

  const totalGridWidth = computed(() => visibleDates.value.length * colWidth.value)

  // Sparse gridline + label arrays, replacing per-day v-for loops in the
  // header and body. At year/5-year zoom levels the buffer holds 1.4k–3.7k
  // dates; iterating that many cells per scroll frame stalls Vue's diff
  // even though most cells produce no DOM. By pre-filtering once per zoom
  // change we render only the few hundred elements that actually paint.

  // Show weekend stripes only when columns are wide enough to make them
  // visually distinct — at quarter+ zoom each weekend day collapses to
  // ≤12px and the alternating shade reads as noise.
  const SHOW_WEEKEND_MIN_COL_WIDTH = 30

  // Vertical gridlines at the current scale's cadence (day / week /
  // fortnight / month / quarter). The leftPx is the right edge of the
  // boundary date — i.e. the start of the next cell.
  const gridlineOffsets = computed(() => {
    const cw = colWidth.value
    const unit = SCALE_CONFIG[zoomLevel.value].gridlineUnit
    const dates = visibleDates.value
    const offsets: Array<{ leftPx: number; key: string }> = []
    for (let i = 0; i < dates.length; i++) {
      if (isGridlineBoundary(dates[i], unit)) {
        offsets.push({ leftPx: (i + 1) * cw, key: dates[i].format('YYYY-MM-DD') })
      }
    }
    return offsets
  })

  // Weekend day positions — only emitted at fine zooms so we don't paint
  // thousands of useless 1-2px stripes at 5-year scale.
  const weekendOffsets = computed(() => {
    const cw = colWidth.value
    if (cw < SHOW_WEEKEND_MIN_COL_WIDTH) return []
    const dates = visibleDates.value
    const offsets: Array<{ leftPx: number; key: string }> = []
    for (let i = 0; i < dates.length; i++) {
      const d = dates[i].day()
      if (d === 0 || d === 6) {
        offsets.push({ leftPx: i * cw, key: dates[i].format('YYYY-MM-DD') })
      }
    }
    return offsets
  })

  // Header minor-row labels — only entries that produce non-empty text.
  // At 'mondays' that's 1-in-7; at 'none' it's empty; at 'weekday-*' modes
  // it's still per-day but those scales have small buffers anyway.
  const minorLabels = computed(() => {
    const cw = colWidth.value
    const mode = SCALE_CONFIG[zoomLevel.value].minorLabel
    if (mode === 'none') return []
    const dates = visibleDates.value
    const showWeekday = mode === 'weekday-full' || mode === 'weekday-short' || mode === 'weekday-letter'
    const labels: Array<{ idx: number; leftPx: number; weekday: string; dayNum: string; key: string }> = []
    for (let i = 0; i < dates.length; i++) {
      const date = dates[i]
      let weekday = ''
      let dayNum = ''
      if (showWeekday) {
        if (mode === 'weekday-full') weekday = date.format('dddd')
        else if (mode === 'weekday-short') weekday = date.format('ddd')
        else weekday = date.format('dd').charAt(0)
      }
      if (mode === 'mondays') {
        if (date.day() === 1) dayNum = date.format('D')
      } else if (mode === 'fortnight') {
        if (isFortnightMonday(date)) dayNum = date.format('D')
      } else if (mode === 'quarter-month') {
        if (date.date() === 1 && date.month() % 3 === 0) dayNum = date.format('MMM')
      } else if (showWeekday) {
        dayNum = date.format('D')
      }
      if (weekday || dayNum) {
        labels.push({ idx: i, leftPx: i * cw, weekday, dayNum, key: date.format('YYYY-MM-DD') })
      }
    }
    return labels
  })

  // Stacked major rows. Each tier is one row of consecutive same-unit spans
  // (e.g. quarter spans, then month spans below). When a parent tier already
  // shows year context, child tiers omit the year from their labels.
  const majorHeaderTiers = computed(() => {
    const tiers = SCALE_CONFIG[zoomLevel.value].majorTiers
    const dates = visibleDates.value
    const cw = colWidth.value
    return tiers.map((unit, tierIdx) => {
      const omitYear = tiers.slice(0, tierIdx).some((t) => t === 'year' || t === 'quarter')
      const spans: Array<{ key: string; leftPx: number; widthPx: number; label: string }> = []
      let i = 0
      while (i < dates.length) {
        const start = dates[i]
        let j = i + 1
        while (j < dates.length) {
          if (unit === 'quarter') {
            if (quarterOf(dates[j]) !== quarterOf(start) || dates[j].year() !== start.year()) break
          } else if (!dates[j].isSame(start, unit)) {
            break
          }
          j++
        }
        let label = ''
        if (unit === 'year') label = start.format('YYYY')
        else if (unit === 'quarter') label = omitYear ? `Q${quarterOf(start)}` : `Q${quarterOf(start)} ${start.format('YYYY')}`
        else label = omitYear ? start.format('MMMM') : start.format('MMM YYYY')

        spans.push({
          key: `${unit}-${start.format('YYYY-MM-DD')}`,
          leftPx: i * cw,
          widthPx: (j - i) * cw,
          label,
        })
        i = j
      }
      return spans
    })
  })

  // ---- Date-range breadcrumb label ----

  const dateRangeLabel = computed(() => {
    switch (zoomLevel.value) {
      case 'day':
        return currentDate.value.format('ddd, MMM D, YYYY')
      case 'week': {
        const start = currentDate.value.startOf('week')
        const end = currentDate.value.endOf('week')
        if (start.month() === end.month()) {
          return `${start.format('D')} - ${end.format('D MMM YYYY')}`
        }
        return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`
      }
      case '2week': {
        const start = currentDate.value.startOf('week')
        const end = start.add(13, 'day')
        if (start.year() !== end.year()) {
          return `${start.format('D MMM YYYY')} - ${end.format('D MMM YYYY')}`
        }
        if (start.month() === end.month()) {
          return `${start.format('D')} - ${end.format('D MMM YYYY')}`
        }
        return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`
      }
      case 'month':
        return currentDate.value.format('MMM YYYY')
      case 'quarter':
        return `Q${quarterOf(currentDate.value)} ${currentDate.value.format('YYYY')}`
      case '6month': {
        const half = currentDate.value.month() < 6 ? 'H1' : 'H2'
        return `${half} ${currentDate.value.format('YYYY')}`
      }
      case 'year':
        return currentDate.value.format('YYYY')
      case '2year': {
        const startYear = currentDate.value.year()
        return `${startYear} - ${startYear + 1}`
      }
      case '5year': {
        const startYear = currentDate.value.year() - 2
        return `${startYear} - ${startYear + 4}`
      }
    }
    // Exhaustively handled above for every `TimelineZoomLevel`. The
    // explicit return satisfies vue/return-in-computed-property and
    // keeps the type as `string` rather than `string | undefined`.
    return ''
  })

  // ---- Imperative scroll-adjustment channel ----

  // Event hook used to ask the grid component to imperatively adjust the
  // body's scrollLeft. `absolute` sets a target; `delta` adds to current
  // (used after extending the buffer leftward to keep the visible content
  // anchored).
  const scrollAdjustmentHook = createEventHook<{ type: 'absolute' | 'delta'; value: number }>()

  // ---- Buffer mechanics ----

  const reAnchorBuffer = (date: dayjs.Dayjs) => {
    const days = SCALE_CONFIG[zoomLevel.value].bufferDays
    bufferStart.value = date.startOf('day').subtract(days, 'day')
    bufferEnd.value = date.startOf('day').add(days, 'day')
  }

  // Both extend* functions are () => void. They mutate buffer refs and emit
  // any scroll-delta the user's viewport needs to stay anchored on the same
  // logical date — extendLeft prepends days (visible content shifts right
  // in pixel space, so scrollLeft must shift right to compensate); extendRight
  // appends days (no compensation unless the trim cap kicks in and bufferStart
  // moves forward, in which case scrollLeft must shift left).
  const extendBufferLeft = () => {
    const cw = colWidth.value
    const days = SCALE_CONFIG[zoomLevel.value].bufferDays
    const maxSpan = days * MAX_BUFFER_MULTIPLIER
    bufferStart.value = bufferStart.value.subtract(days, 'day')
    scrollAdjustmentHook.trigger({ type: 'delta', value: days * cw })
    // Trim the far (right) edge if total span exceeds the cap. Safe because
    // edge-extension only fires when the user is near the *opposite* edge,
    // so the trimmed dates aren't visible.
    const span = bufferEnd.value.diff(bufferStart.value, 'day')
    if (span > maxSpan) {
      bufferEnd.value = bufferEnd.value.subtract(span - maxSpan, 'day')
    }
  }

  const extendBufferRight = () => {
    const cw = colWidth.value
    const days = SCALE_CONFIG[zoomLevel.value].bufferDays
    const maxSpan = days * MAX_BUFFER_MULTIPLIER
    bufferEnd.value = bufferEnd.value.add(days, 'day')
    // Trim the far (left) edge if total span exceeds the cap. Adjust
    // scrollLeft so the visible content stays put after the trim.
    const span = bufferEnd.value.diff(bufferStart.value, 'day')
    if (span > maxSpan) {
      const trimDays = span - maxSpan
      bufferStart.value = bufferStart.value.add(trimDays, 'day')
      scrollAdjustmentHook.trigger({ type: 'delta', value: -trimDays * cw })
    }
  }

  // ---- Scroll target requests ----

  // Compute and emit a scroll target so `date` ends up centred in the
  // viewport. If viewport width hasn't been measured yet (initial mount,
  // before the grid container has rendered), queue the date and re-issue
  // once the width is known.
  const _pendingScrollDate = ref<dayjs.Dayjs | null>(null)

  const requestScrollToDate = (date: dayjs.Dayjs) => {
    if (viewportWidth.value <= 0) {
      _pendingScrollDate.value = date
      return
    }
    const dayOffset = date.diff(bufferStart.value, 'day')
    const target = dayOffset * colWidth.value + colWidth.value / 2 - viewportWidth.value / 2
    scrollAdjustmentHook.trigger({ type: 'absolute', value: Math.max(0, target) })
  }

  // Drain pending scroll once the grid measures itself.
  watch(viewportWidth, (w) => {
    if (w > 0 && _pendingScrollDate.value) {
      const date = _pendingScrollDate.value
      _pendingScrollDate.value = null
      requestScrollToDate(date)
    }
  })

  // Called from the grid on every scroll event. Updates derived state and
  // grows the buffer when the user nears an edge.
  const onScrollUpdate = (newScrollLeft: number) => {
    scrollLeft.value = newScrollLeft

    // Update currentDate from viewport center
    if (viewportWidth.value > 0 && colWidth.value > 0) {
      const centerPx = newScrollLeft + viewportWidth.value / 2
      const dayIdx = Math.floor(centerPx / colWidth.value)
      const newDate = bufferStart.value.add(dayIdx, 'day')
      if (!newDate.isSame(currentDate.value, 'day')) {
        currentDate.value = newDate
      }
    }

    // Edge-driven extension
    if (newScrollLeft < EXTEND_THRESHOLD_PX) {
      extendBufferLeft()
    } else if (viewportWidth.value > 0 && newScrollLeft + viewportWidth.value > totalGridWidth.value - EXTEND_THRESHOLD_PX) {
      extendBufferRight()
    }
  }

  const setViewportWidth = (w: number) => {
    viewportWidth.value = w
  }

  // ---- Zoom level changes ----

  // Re-anchor + scroll when zoom level changes — keep the *fractional* date
  // at viewport centre stable across the scale switch.
  //
  // `currentDate` is rounded to whole days (via Math.floor in onScrollUpdate),
  // so using it as the centre source loses up to half a day's worth of
  // pixels. At fine zoom (day = 160 px) that's a visible 80 px shift on
  // each scale change. Reading `scrollLeft + viewportWidth/2` against the
  // OLD colWidth gives the exact fractional day offset, which we then
  // re-position to viewport centre in the new layout.
  //
  // We also project `scrollLeft.value` synchronously so derived layout
  // (bar labels, viewport tests) is consistent with new buffer/colWidth on
  // the first frame. The imperative DOM scroll catches up in nextTick to
  // handle scrollWidth changes (which can clamp the sync write if the new
  // grid is wider than the old DOM had room for).
  watch(zoomLevel, (_newLevel, oldLevel) => {
    const oldColWidth = SCALE_CONFIG[oldLevel].colWidth
    const newBufferDays = SCALE_CONFIG[zoomLevel.value].bufferDays

    // When the viewport has been measured (the normal case — user clicked
    // the zoom dropdown), read the *fractional* date at viewport centre
    // off the old layout. When it hasn't been measured yet (initial mount
    // via the cache-restore path, before Grid measures itself), fall back
    // to the current `currentDate` so we don't stomp it to bufferStart;
    // the pending-scroll mechanism re-issues once the viewport arrives.
    let center: dayjs.Dayjs
    let subdayFrac = 0
    if (viewportWidth.value > 0 && oldColWidth > 0) {
      const fracOffset = (scrollLeft.value + viewportWidth.value / 2) / oldColWidth
      const wholeDays = Math.floor(fracOffset)
      subdayFrac = fracOffset - wholeDays
      center = bufferStart.value.add(wholeDays, 'day')
    } else {
      center = currentDate.value
    }

    currentDate.value = center
    reAnchorBuffer(center)

    // After reAnchor: center is exactly `newBufferDays` whole days from
    // the new bufferStart. Add `subdayFrac` to keep the fractional position.
    const computeTarget = () => (newBufferDays + subdayFrac) * colWidth.value - viewportWidth.value / 2

    if (viewportWidth.value > 0) {
      scrollLeft.value = Math.max(0, computeTarget())
      nextTick(() => {
        if (viewportWidth.value <= 0) return
        scrollAdjustmentHook.trigger({ type: 'absolute', value: Math.max(0, computeTarget()) })
      })
    } else {
      // No viewport yet — defer to the pending-scroll path so the date
      // ends up centred once Grid measures itself.
      _pendingScrollDate.value = center
    }
  })

  // ---- Cross-view persistence ----

  // Track the last view ID we cached state for, so we can detect when the
  // active view switches and we need to restore.
  let _lastCachedViewId: string | undefined

  // Debounced cache write. `_lastCachedViewId` updates synchronously so the
  // view-id watcher's "is this the view we were just navigating?" check
  // stays correct even if the user switches views before the next flush.
  // The Map write itself can lag without losing state — we capture values
  // at change time, not at flush time.
  const _persistViewState = useDebounceFn((vid: string, isoDate: string, zoom: TimelineZoomLevel) => {
    lruViewCacheSet(vid, { currentDate: isoDate, zoomLevel: zoom })
  }, 250)

  watch([currentDate, zoomLevel], () => {
    const vid = viewId.value
    if (!vid) return
    _lastCachedViewId = vid
    _persistViewState(vid, currentDate.value.toISOString(), zoomLevel.value)
  })

  // When the active view changes (e.g., user switches back to this feature
  // from another view), restore the cached navigation state for that view.
  watch(
    () => viewId.value,
    (newViewId) => {
      if (!newViewId || newViewId === _lastCachedViewId) return
      const cached = lruViewCacheGet(newViewId)
      if (cached) {
        // Cached zoom may be from a feature that allows more levels than
        // ours (e.g. Timeline cached 'day', then user opens a Gantt view
        // that doesn't expose 'day'). Fall back to the current default
        // when the cached value isn't allowed.
        if (allowedZoomLevels.includes(cached.zoomLevel)) {
          zoomLevel.value = cached.zoomLevel
        }
        const date = dayjs(cached.currentDate)
        currentDate.value = date
        selectedDate.value = date
        reAnchorBuffer(date)
        if (viewportWidth.value > 0) {
          const dayOffset = date.diff(bufferStart.value, 'day')
          const target = dayOffset * colWidth.value + colWidth.value / 2 - viewportWidth.value / 2
          scrollLeft.value = Math.max(0, target)
        }
        nextTick(() => requestScrollToDate(date))
        _lastCachedViewId = newViewId
      }
    },
    { immediate: true },
  )

  // ---- Navigation ----

  const goToDate = (date: dayjs.Dayjs) => {
    currentDate.value = date
    selectedDate.value = date

    const inBuffer = !date.isBefore(bufferStart.value, 'day') && !date.isAfter(bufferEnd.value, 'day')
    if (inBuffer) {
      requestScrollToDate(date)
    } else {
      reAnchorBuffer(date)
      nextTick(() => requestScrollToDate(date))
    }
  }

  const goToToday = () => goToDate(dayjs())

  const navigateNext = () => {
    const cfg = SCALE_CONFIG[zoomLevel.value]
    goToDate(currentDate.value.add(cfg.navAmount, cfg.navUnit))
  }

  const navigatePrev = () => {
    const cfg = SCALE_CONFIG[zoomLevel.value]
    goToDate(currentDate.value.subtract(cfg.navAmount, cfg.navUnit))
  }

  const setZoomLevel = (level: TimelineZoomLevel) => {
    if (!allowedZoomLevels.includes(level)) return
    zoomLevel.value = level
  }

  // ---- First-visit gating ----
  // Consumers' "initial view" logic (centre on closest record vs today) should
  // run exactly once per view-id per session, regardless of whether the user
  // has interacted yet. We expose the gate so the data store can keep that
  // logic next to the data, while the axis owns the cache.

  const isViewInitialized = (vid: string | undefined): boolean => {
    if (!vid) return true
    return _initializedViews.has(vid) || _viewStateCache.has(vid)
  }

  const markViewInitialized = (vid: string | undefined) => {
    if (vid) _initializedViews.add(vid)
  }

  return {
    // State refs
    zoomLevel,
    currentDate,
    selectedDate,
    bufferStart,
    bufferEnd,
    scrollLeft,
    viewportWidth,

    // Computed
    colWidth,
    visibleDates,
    totalGridWidth,
    gridlineOffsets,
    weekendOffsets,
    minorLabels,
    majorHeaderTiers,
    dateRangeLabel,

    // Constants the consumer might want
    SCALE_CONFIG,
    allowedZoomLevels,

    // Buffer + scroll
    reAnchorBuffer,
    extendBufferLeft,
    extendBufferRight,
    requestScrollToDate,
    onScrollUpdate,
    setViewportWidth,
    onScrollAdjustment: scrollAdjustmentHook.on,

    // Navigation
    goToDate,
    goToToday,
    navigateNext,
    navigatePrev,
    setZoomLevel,

    // First-visit gating
    isViewInitialized,
    markViewInitialized,
  }
}

export type UseDateAxisState = ReturnType<typeof useDateAxisState>
